import type { Player, Team } from '@/types/tournament.model';
import { getTeamName } from '@/utils/team-utils';
import { cn } from '@/lib/utils';
import { PlayerInfoPopover } from '../PlayerInfoPopover';

function getPlayerElo(player: Player, sportUid?: string): number | null {
  const rankings = player.rankings ?? [];
  let elo: number | undefined;
  if (sportUid) {
    elo = rankings.find((r) => r.sport?.uid === sportUid)?.elo;
  }
  if (elo === undefined && rankings.length > 0) {
    elo = rankings[0].elo;
  }
  return elo !== undefined ? Math.round(elo) : null;
}

function getTeamAverageElo(team: Team, sportUid?: string): number | null {
  const playerElos: number[] = [];
  for (const p of team.players ?? []) {
    const elo = getPlayerElo(p, sportUid);
    if (elo !== null) playerElos.push(elo);
  }
  if (playerElos.length === 0) return null;
  return Math.round(playerElos.reduce((a, b) => a + b, 0) / playerElos.length);
}

function getInitialsFromFullName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const p = parts[0];
    return (p[0] + (p[1] || '')).toUpperCase();
  }
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  );
}

function getPlayerInitialsFromFallbackTeamName(
  teamName: string,
  playerIndex: number,
): string {
  const players = teamName.split(' / ');
  if (players[playerIndex]) {
    return getInitialsFromFullName(players[playerIndex]);
  }
  return playerIndex === 0 ? 'P1' : 'P2';
}

function SwapTeamRowContent({
  team,
  teamName,
  tournamentSportUid,
}: {
  team: Team;
  teamName: string;
  tournamentSportUid?: string;
}) {
  const avgElo = getTeamAverageElo(team, tournamentSportUid);
  const eloLabel =
    avgElo !== null
      ? `Elo promedio del equipo: ${avgElo}`
      : 'Elo promedio del equipo: —';

  const players = team.players ?? [];
  const showPlaceholders = players.length < 2;

  const renderAvatar = (playerIndex: 0 | 1) => {
    const player = players[playerIndex];
    const url = player?.profileImageUrl;
    const fallbackBg = playerIndex === 0 ? 'bg-slate-600' : 'bg-slate-500';
    const initials = player
      ? getInitialsFromFullName(player.name)
      : getPlayerInitialsFromFallbackTeamName(teamName, playerIndex);
    const avatar = url ? (
      <img
        src={url}
        alt=""
        className="h-8 w-8 rounded-full border-2 border-white object-cover"
      />
    ) : (
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-white ${fallbackBg}`}
      >
        {initials}
      </div>
    );
    return player ? (
      <PlayerInfoPopover player={player}>{avatar}</PlayerInfoPopover>
    ) : (
      avatar
    );
  };

  return (
    <div className="flex w-full min-w-0 items-start gap-3">
      <div className="flex shrink-0 -space-x-2 pt-0.5">
        {renderAvatar(0)}
        {showPlaceholders && (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/40 bg-muted/40 text-[10px] font-medium text-muted-foreground"
            aria-hidden
          >
            —
          </div>
        )}
        {!showPlaceholders && renderAvatar(1)}
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        {players.length === 0 ? (
          <p className="text-sm text-muted-foreground">{teamName}</p>
        ) : (
          players.map((player, idx) => {
            const pe = getPlayerElo(player, tournamentSportUid);
            const eloText = pe !== null ? String(pe) : '—';
            return (
              <div
                key={player.id || idx}
                className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-2"
              >
                <PlayerInfoPopover player={player}>
                  <span className="cursor-pointer text-left text-sm font-medium text-foreground hover:text-primary hover:underline">
                    {player.name?.trim() || '—'}
                  </span>
                </PlayerInfoPopover>
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  Elo {eloText}
                </span>
              </div>
            );
          })
        )}
        <p className="border-t border-border pt-1.5 text-xs font-medium text-muted-foreground">
          {eloLabel}
        </p>
      </div>
    </div>
  );
}

export function SwapTeamSelectButton({
  team,
  selected,
  onSelect,
  tournamentSportUid,
  hasPlayed,
}: {
  team: Team;
  selected: boolean;
  onSelect: () => void;
  tournamentSportUid?: string;
  hasPlayed: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={hasPlayed}
      className={cn(
        'flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        hasPlayed ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/80',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border',
      )}
    >
      <div className="min-w-0 flex-1">
        <SwapTeamRowContent
          team={team}
          teamName={getTeamName(team)}
          tournamentSportUid={tournamentSportUid}
        />
      </div>
      {hasPlayed && (
        <span className="shrink-0 rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          Ya jugó
        </span>
      )}
    </button>
  );
}

export function SwapTeamSummaryBlock({
  team,
  label,
  tournamentSportUid,
}: {
  team: Team;
  /** Where the team sits today (its group or bracket position); omitted when both share it. */
  label?: string;
  tournamentSportUid?: string;
}) {
  const teamName = getTeamName(team);
  return (
    <div className="flex items-start gap-3 rounded-md border border-border bg-background/80 px-3 py-2">
      <div className="min-w-0 flex-1">
        <SwapTeamRowContent
          team={team}
          teamName={teamName}
          tournamentSportUid={tournamentSportUid}
        />
      </div>
      {label && (
        <span className="shrink-0 pt-1 text-xs text-muted-foreground">
          {label}
        </span>
      )}
    </div>
  );
}
